import {FilterOperator, UseFiltersProps} from '@cervello/use-filters';
import React from 'react';

export interface Sort {
  [key: string]: SortOrders | undefined;
}

export type ListResponse<T> = {
  result: T[];
  data?: T[];
  page: string | number;
  limit: number;
  count: number;
};

export interface Props<T, F> {
  initialData?: T[];
  maintainPages?: boolean;
  disableInitialLoad?: boolean;
  fetchData: (args: {
    query: string;
    cancellationToken: string | null;
  }) => Promise<ListResponse<T>> | undefined;
  initialPagination?: Partial<Pagination>;
  initialFilters?: UseFiltersProps<F>;
  filters?: Array<FilterState<F>>;
  initialSort?: Sort;
  initialSearch?: string | undefined;
  initialQueryParams?: {[key: string]: any};
  initialAggregation?: Aggregation;
  initialTimeWindow?: TimeWindow;
  realTime?: {
    enable: boolean;
    topic: string;
    config: {
      token: string;
      baseUrl: string;
      projectId: string;
    };
    callback?: (data: T[]) => void;
  };
  cancelController?: () => {
    signal: any;
    cancel: () => void;
  };
  restController?: {
    generateCancelToken: () => string;
    cancel: () => void;
  };
}

export interface State<T, F> {
  data: T[];
  isLoading: boolean;
  pagination: Pagination;
  sort?: Sort;
  search?: string | undefined;
  filters?: FilterState<F>[];
  aggregation?: Aggregation;
  timeWindow?: TimeWindow;
  queryParams?: {[key: string]: unknown; newPagination?: Partial<Pagination>};
}

export enum Actions {
  FETCHING,
  SET_QUERY_PARAM,
  RESET_QUERY_PARAMS,
  RESET_QUERY,
  // SET_SEARCH,
  RESET_SEARCH,
  TOGGLE_SORT,
  SET_SORT_OPTIONS,
  SET_AGGREGATION,
  SET_TIMEWINDOW,
  SET_PAGINATION,
  SET_DATA,
  UPDATE_DATA,
  SET_FILTERS,
  SET_SEARCH_VALUE,
  TOGGLE_FETCHING,
}

export type Action<T, F = any> =
  | {
      type: Actions.FETCHING;
    }
  | {
      type: Actions.TOGGLE_FETCHING;
    }
  // | {
  //     type: Actions.SET_SEARCH;
  //     payload: string | undefined;
  //   }
  | {
      type: Actions.RESET_SEARCH;
      payload: string | undefined;
    }
  | {
      type: Actions.TOGGLE_SORT;
      payload: {key: string};
    }
  | {
      type: Actions.SET_QUERY_PARAM;
      payload: {[key: string]: unknown; newPagination?: Partial<Pagination>};
    }
  | {
      type: Actions.RESET_QUERY_PARAMS;
    }
  | {
      type: Actions.RESET_QUERY;
      payload: {
        pagination: Props<any, any>['initialPagination'];
        sort: Props<any, any>['initialSort'];
        search: Props<any, any>['initialSearch'];
        queryParams: Props<any, any>['initialQueryParams'];
      };
    }
  | {
      type: Actions.SET_SORT_OPTIONS;
      payload: {sortOption: Sort; initialPagination?: Partial<Pagination>};
    }
  | {
      type: Actions.SET_AGGREGATION;
      payload: Aggregation;
    }
  | {
      type: Actions.SET_TIMEWINDOW;
      payload: TimeWindow;
    }
  | {
      type: Actions.SET_PAGINATION;
      payload: Partial<Pagination> | ((pagination: Pagination) => Partial<Pagination>);
    }
  | {
      type: Actions.SET_DATA;
      payload: {
        data: T[];
        currentQueryRef: React.MutableRefObject<string>;
        requestQuery: string;
        pagination: Pick<Pagination, 'count'> & {
          prevPagination: Pagination;
          maintainPages: boolean;
        };
      };
    }
  | {
      type: Actions.UPDATE_DATA;
      payload: T[];
    }
  | {
      type: Actions.SET_FILTERS;
      payload: {
        filters: FilterState<F>[];
        newPagination?: Partial<Pagination>;
      };
    }
  | {
      type: Actions.SET_SEARCH_VALUE;
      payload?: string;
    };

export interface Aggregation {
  func: string;
  interval: {
    unit: string;
    value: number;
  };
}
export enum SortOrders {
  ASC = 'asc',
  DESC = 'desc',
}

export type FilterValue = string | number | boolean | null | string[] | number[];

export interface TimeWindow {
  type: 'REAL_TIME' | 'HISTORY';
  rangeOptions?: {
    start: number;
    end: number;
  };
  startValues?: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}

export interface Pagination {
  page: number;
  limit: number;
  count: number;
}
export type FilterState<Filters> = {
  key: keyof Filters;
  operator: FilterOperator;
  value: FilterValue;
};
